/**
 * Human-friendly display names and descriptions for agent tools.
 *
 * SSOT for tool presentation across the UI — used by ChatPanel's
 * live tool call traces and any future tool-related UI.
 *
 * SOLID:
 *   SRP — this file only maps tool IDs to display data.
 *   OCP — add a new tool by appending to TOOL_DISPLAY. No component changes.
 *   DIP — components depend on the ToolDisplayInfo interface, not on
 *         tool names or specific formatting logic.
 *
 * To add a new tool: add an entry to TOOL_DISPLAY below. The UI picks
 * it up automatically. Unknown tools fall back to the raw tool name.
 */

export interface ToolDisplayInfo {
  /** Short human label (e.g. "Reading section") */
  label: string;
  /** Verb phrase for the active/in-progress state (e.g. "Reading") */
  activeVerb: string;
  /** Verb phrase for the completed state (e.g. "Read") */
  completedVerb: string;
  /** Which parameter to show as context (e.g. "section_number") */
  contextParam?: string;
  /** Optional second parameter for richer context */
  contextParam2?: string;
}

/** Context params that name a section, shown as "§9.2"; any other param is
 *  shown as written (``query`` in quotes). */
const SECTION_PARAMS: ReadonlySet<string> = new Set(["section_number", "section_label", "module_id", "title"]);

/**
 * Tool display registry. Keyed by the tool name as sent by the agent.
 */
const TOOL_DISPLAY: Record<string, ToolDisplayInfo> = {
  // Document tools
  doc_list_sections: {
    label: "Document structure",
    activeVerb: "Scanning",
    completedVerb: "Scanned",
  },
  doc_read_section: {
    label: "Section content",
    activeVerb: "Reading",
    completedVerb: "Read",
    contextParam: "section_number",
  },
  doc_write_section: {
    label: "Section update",
    activeVerb: "Writing",
    completedVerb: "Updated",
    contextParam: "section_number",
  },
  doc_get_provenance: {
    label: "Generation provenance",
    activeVerb: "Checking provenance of",
    completedVerb: "Checked provenance of",
    contextParam: "section_number",
  },
  doc_diff: {
    label: "Change detection",
    activeVerb: "Comparing",
    completedVerb: "Compared",
    contextParam: "section_number",
  },
  doc_search: {
    label: "Document search",
    activeVerb: "Searching for",
    completedVerb: "Found",
    contextParam: "query",
  },
  // Live Word document tools (chat agent, run in the add-in over the bridge)
  doc_get_runtime_context: {
    label: "Cursor context",
    activeVerb: "Checking",
    completedVerb: "Checked",
  },
  doc_find_positions: {
    label: "Find in document",
    activeVerb: "Searching for",
    completedVerb: "Searched for",
    contextParam: "query",
  },
  doc_read_at_positions: {
    label: "Passages",
    activeVerb: "Reading",
    completedVerb: "Read",
  },
  doc_list_tables: {
    label: "Tables",
    activeVerb: "Listing",
    completedVerb: "Listed",
  },
  doc_get_table: {
    label: "Table",
    activeVerb: "Reading",
    completedVerb: "Read",
  },
  doc_table_capabilities: {
    label: "Table tools",
    activeVerb: "Checking",
    completedVerb: "Checked",
  },
  doc_write_at_positions: {
    label: "Document edit",
    activeVerb: "Making",
    completedVerb: "Made",
  },
  doc_replace_text: {
    label: "Text replacement",
    activeVerb: "Making",
    completedVerb: "Made",
  },
  doc_propose_changes: {
    label: "Edit proposal",
    activeVerb: "Drafting",
    completedVerb: "Drafted",
  },
  doc_merge_table_cells: { label: "Cell merge", activeVerb: "Applying", completedVerb: "Applied" },
  doc_split_table_cell: { label: "Cell split", activeVerb: "Applying", completedVerb: "Applied" },
  doc_insert_table_rows: { label: "Table rows", activeVerb: "Inserting", completedVerb: "Inserted" },
  doc_delete_table_rows: { label: "Table rows", activeVerb: "Deleting", completedVerb: "Deleted" },
  doc_insert_table_columns: { label: "Table columns", activeVerb: "Inserting", completedVerb: "Inserted" },
  doc_delete_table_columns: { label: "Table columns", activeVerb: "Deleting", completedVerb: "Deleted" },
  doc_set_table_borders: { label: "Table borders", activeVerb: "Setting", completedVerb: "Set" },
  doc_set_table_shading: { label: "Table shading", activeVerb: "Setting", completedVerb: "Set" },
  doc_resize_table_columns: { label: "Column widths", activeVerb: "Resizing", completedVerb: "Resized" },
  doc_set_table_cell_values: { label: "Table cells", activeVerb: "Filling", completedVerb: "Filled" },
  doc_insert_table_rows_and_set_values: { label: "Table rows", activeVerb: "Adding", completedVerb: "Added" },

  // Retrieval tools (Asthra)
  find_section: {
    label: "Source lookup",
    activeVerb: "Looking up",
    completedVerb: "Found",
    contextParam: "section_label",
    contextParam2: "file_label",
  },
  find_table: {
    label: "Table lookup",
    activeVerb: "Looking up",
    completedVerb: "Found",
    contextParam: "table_label",
    contextParam2: "file_label",
  },
  search_library: {
    label: "Library search",
    activeVerb: "Searching",
    completedVerb: "Searched",
    contextParam: "query",
  },
  get_document_inventory: {
    label: "Document inventory",
    activeVerb: "Loading contents of",
    completedVerb: "Loaded",
    contextParam: "file_label",
  },
  // Retrieval sub-agent internals (retrieve_from_library's own loop)
  list_documents: {
    label: "Document list",
    activeVerb: "Listing documents matching",
    completedVerb: "Listed documents matching",
    contextParam: "name_filter",
  },
  list_folders: {
    label: "Library folders",
    activeVerb: "Listing folders",
    completedVerb: "Listed folders",
  },
  submit_retrieval: {
    label: "Retrieval result",
    activeVerb: "Returning results",
    completedVerb: "Returned results",
  },
  find_image: {
    label: "Image lookup",
    activeVerb: "Finding image in",
    completedVerb: "Found image in",
    contextParam: "file_label",
  },

  // Literature tools
  search_literature: {
    label: "Literature search",
    activeVerb: "Searching PubMed for",
    completedVerb: "Found results for",
    contextParam: "query",
  },

  // Provenance tool
  query_provenance: {
    label: "Run provenance",
    activeVerb: "Querying",
    completedVerb: "Retrieved",
    contextParam: "kind",
  },

  // User input
  request_user_input: {
    label: "Waiting for input",
    activeVerb: "Asking",
    completedVerb: "Received input",
    contextParam: "question",
  },

  // Abbreviations table (/abbreviations command — client-driven stream)
  scan_sections: {
    label: "the draft",
    activeVerb: "Reading",
    completedVerb: "Read",
  },
  extract_abbreviations: {
    label: "abbreviations",
    activeVerb: "Extracting",
    completedVerb: "Extracted",
  },
  infer_abbreviations: {
    label: "undefined acronyms",
    activeVerb: "Resolving",
    completedVerb: "Resolved",
  },

  // Draft submission
  submit_draft: {
    label: "Draft submission",
    activeVerb: "Submitting",
    completedVerb: "Submitted",
    contextParam: "title",
  },
  submit_rewrite: {
    label: "Rewrite submission",
    activeVerb: "Submitting rewrite",
    completedVerb: "Submitted rewrite",
  },

  // AsthraAgent toolbelt — the unified drafting / rewrite loop's tools. These
  // used to live only in the spine's private verb map; one registry now feeds
  // the spine subline, the live activity feed, the chat rail and Action History.
  retrieve_from_library: {
    label: "Library retrieval",
    activeVerb: "Retrieving",
    completedVerb: "Retrieved",
    contextParam: "query",
  },
  view_document_pages: {
    label: "Page images",
    activeVerb: "Viewing pages of",
    completedVerb: "Viewed pages of",
    contextParam: "doc_name",
  },
  // Deterministic read tools (lead + child drafters) — verbatim, no model inside.
  read_section: {
    label: "Section read",
    activeVerb: "Reading",
    completedVerb: "Read",
    contextParam: "section_label",
    contextParam2: "doc_name",
  },
  read_table: {
    label: "Table read",
    activeVerb: "Reading table",
    completedVerb: "Read table",
    contextParam: "table_label",
    contextParam2: "doc_name",
  },
  semantic_search: {
    label: "Locate in library",
    activeVerb: "Locating",
    completedVerb: "Located",
    contextParam: "query",
    contextParam2: "doc_name",
  },
  find_many_files: {
    label: "Find files",
    activeVerb: "Finding files for",
    completedVerb: "Found files for",
    contextParam: "text",
  },
  guidance_lookup: {
    label: "Guidance lookup",
    activeVerb: "Looking up guidance for",
    completedVerb: "Looked up guidance for",
    contextParam: "module_id",
  },
  search_regulatory_sites: {
    label: "Regulator sites",
    activeVerb: "Searching regulator sites for",
    completedVerb: "Searched regulator sites for",
    contextParam: "query",
  },
  create_artefact: {
    label: "Derived table",
    activeVerb: "Building table",
    completedVerb: "Built table",
    contextParam: "save_as",
  },
  search_across_documents: {
    label: "Cross-document search",
    activeVerb: "Searching",
    completedVerb: "Searched",
    contextParam: "query",
  },
  get_document_tree: {
    label: "Document tree",
    activeVerb: "Scanning",
    completedVerb: "Scanned",
    contextParam: "file_label",
  },
  get_document_metadata: {
    label: "Document metadata",
    activeVerb: "Scanning",
    completedVerb: "Scanned",
    contextParam: "file_label",
  },
  get_asset_inventory: {
    label: "Asset inventory",
    activeVerb: "Indexing",
    completedVerb: "Indexed",
    contextParam: "file_label",
  },
  retrieve_module_instructions: {
    label: "Section instructions",
    activeVerb: "Loading instructions for",
    completedVerb: "Loaded instructions for",
    contextParam: "module_id",
  },
  get_prior_section: {
    label: "Prior section",
    activeVerb: "Reading prior section",
    completedVerb: "Read prior section",
    contextParam: "section_number",
  },
  semantic_match: {
    label: "Semantic match",
    activeVerb: "Reading",
    completedVerb: "Read",
    contextParam: "query",
  },
  retrieve_and_extract: {
    label: "Extraction",
    activeVerb: "Reading",
    completedVerb: "Read",
    contextParam: "query",
  },
  dynamic_retrieval: {
    label: "Retrieval",
    activeVerb: "Reading",
    completedVerb: "Read",
    contextParam: "query",
  },
  generate_text: {
    label: "Drafting",
    activeVerb: "Drafting",
    completedVerb: "Drafted",
  },

  // Workspace notes + long drafts (goal-driven drafter). The server sends the
  // note key / part number as the call's query (``asthra_agent._tool_query``).
  write_note: {
    label: "Note",
    activeVerb: "Saving note",
    completedVerb: "Saved note",
    contextParam: "key",
  },
  read_note: {
    label: "Note",
    activeVerb: "Reading note",
    completedVerb: "Read note",
    contextParam: "key",
  },
  list_notes: {
    label: "Notes",
    activeVerb: "Checking notes",
    completedVerb: "Checked notes",
    contextParam: "prefix",
  },
  search_notes: {
    label: "Notes search",
    activeVerb: "Searching notes for",
    completedVerb: "Searched notes for",
    contextParam: "query",
  },
  append_draft_part: {
    label: "Draft part",
    activeVerb: "Writing draft part",
    completedVerb: "Wrote draft part",
    contextParam: "part",
  },
  mark_checklist_items: {
    label: "Checklist",
    activeVerb: "Ticking checklist",
    completedVerb: "Ticked checklist",
  },
  calculate: {
    label: "Calculation",
    activeVerb: "Calculating",
    completedVerb: "Calculated",
    contextParam: "operation",
  },

  // Global regulatory guidance libraries.
  search_guidance: {
    label: "Guidance search",
    activeVerb: "Searching guidance for",
    completedVerb: "Searched guidance for",
    contextParam: "query",
  },
  list_guidance_documents: {
    label: "Guidance documents",
    activeVerb: "Listing guidance on",
    completedVerb: "Listed guidance on",
    contextParam: "topic",
  },
  read_guidance: {
    label: "Guidance read",
    activeVerb: "Reading guidance",
    completedVerb: "Read guidance",
    contextParam: "document",
  },
};

/**
 * Get display info for a tool. Falls back to a generated display
 * from the raw tool name (e.g. "doc_read_section" → "Doc read section").
 */
export function getToolDisplay(toolName: string): ToolDisplayInfo {
  if (TOOL_DISPLAY[toolName]) {
    return TOOL_DISPLAY[toolName];
  }
  // Fallback: convert snake_case to readable label
  const readable = toolName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return {
    label: readable,
    activeVerb: "Running",
    completedVerb: "Completed",
  };
}

/**
 * Build a human-friendly description of a tool call.
 *
 * @param toolName - The raw tool name
 * @param input - The tool's input parameters
 * @param isComplete - Whether the call has finished
 * @returns A string like "Reading section 4.0" or "Searched library for 'OCT imaging'"
 */
export function describeToolCall(toolName: string, input: Record<string, unknown>, isComplete: boolean): string {
  const info = getToolDisplay(toolName);
  const verb = isComplete ? info.completedVerb : info.activeVerb;

  // Build context from parameters
  const parts: string[] = [verb];
  if (info.contextParam && input[info.contextParam]) {
    const val = String(input[info.contextParam]).slice(0, 50);
    if (info.contextParam2 && input[info.contextParam2]) {
      parts.push(`"${val}" in ${String(input[info.contextParam2]).slice(0, 40)}`);
    } else {
      parts.push(contextText(info.contextParam, val));
    }
  } else if (!verb.toLowerCase().endsWith(info.label.toLowerCase())) {
    // "Wrote draft part" already names the thing; "draft part" again would
    // read "Wrote draft part draft part".
    parts.push(info.label.toLowerCase());
  }

  return parts.join(" ");
}

/** One context value as shown after the verb: `"query"`, `§9.2` or the plain value. */
function contextText(param: string, val: string): string {
  if (param === "query") return `"${val}"`;
  return SECTION_PARAMS.has(param) ? `§${val}` : val;
}

/** Lowercase the first character only — keeps inner casing like
 *  "PubMed" intact when a verb phrase is dropped after "Asthra is ". */
function lowerFirst(s: string): string {
  return s.length > 0 ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

/**
 * Build the present-tense phrase shown in a live "Asthra is …" status
 * line, e.g. `searching PubMed for "OCT imaging"` or `reading §9.2`.
 *
 * Reuses the same SSOT verbs/context as {@link describeToolCall} so the
 * streaming status line and the completed tool-call trace never drift
 * apart. The returned phrase is lowercased at the first character so it
 * reads naturally after "Asthra is ".
 *
 * @param toolName Raw tool name from the SSE stream.
 * @param input Tool input parameters (carries the context param).
 * @param surface Optional retrieval surface ("online", "doc", …). Adds a
 *   destination qualifier ("searching online") only when the verb is a
 *   single bare word that doesn't already name one.
 */
export function buildLiveStatusPhrase(toolName: string, input?: Record<string, unknown>, surface?: string): string {
  const info = getToolDisplay(toolName);
  const ctxVal = info.contextParam && input ? input[info.contextParam] : undefined;
  const hasContext = ctxVal != null && String(ctxVal).trim() !== "";
  if (hasContext) {
    return lowerFirst(describeToolCall(toolName, input ?? {}, false));
  }
  // An unmapped tool's verb is the bare "Running": name the tool instead.
  const verb = TOOL_DISPLAY[toolName] ? lowerFirst(info.activeVerb) : `running ${info.label.toLowerCase()}`;
  if (surface === "online" && !verb.includes(" ")) {
    return `${verb} online`;
  }
  return verb;
}

/**
 * Fit a free-form query on one line: strip newlines, collapse whitespace,
 * ellipsise over the limit.
 */
export function truncateQuery(s: string, limit = 60): string {
  const cleaned = s.replace(/\s+/g, " ").trim();
  return cleaned.length > limit ? `${cleaned.slice(0, limit - 1)}…` : cleaned;
}

/**
 * Inject a pre-flattened query string under the tool's context parameter.
 *
 * The run SSE stream carries ``tool_query`` as one string (``asthra_agent._tool_query``
 * flattens the input), not the input dict the chat rail gets — so the spine
 * cannot call {@link describeToolCall} directly. Re-keying the string under the
 * tool's ``contextParam`` lets both surfaces share the same verbs.
 */
export function inputFromQuery(toolName: string, query?: string | null): Record<string, unknown> | undefined {
  if (query == null || String(query).trim() === "") return undefined;
  const info = getToolDisplay(toolName);
  return { [info.contextParam ?? "query"]: truncateQuery(String(query)) };
}

/** Live phrase for a ``tool_call_start`` off the run stream, e.g. `looking up §9.2`. */
export function buildLiveStatusPhraseFromQuery(toolName: string, query?: string | null, surface?: string): string {
  return buildLiveStatusPhrase(toolName, inputFromQuery(toolName, query), surface);
}

/** Completed phrase for a ``tool_call_result`` off the run stream, e.g. `Found §9.2`. */
export function describeToolCallFromQuery(toolName: string, query: string | null | undefined, isComplete: boolean): string {
  return describeToolCall(toolName, inputFromQuery(toolName, query) ?? {}, isComplete);
}

/** Span role (server ``csr_sim.agent_span``) → the kind of agent. Mirrors
 *  ``_AGENT_KIND`` in ``server/adapters/agent_event_format.py``. */
const AGENT_KIND_OF_ROLE: Record<string, string> = {
  superintendent: "Superintendent",
  lead: "Superintendent",
  child: "Drafter",
  main: "Drafter",
  retrieval: "Librarian",
  cascade: "Librarian",
  reviewer: "Reviewer",
  resolve: "Resolver",
};

/**
 * Who emitted an event — "Drafter 2/5", "Librarian (Drafter 2/5)",
 * "Superintendent" — from the span fields the server merges into every payload
 * (``agent_kind`` / ``role``, ``node_index`` / ``node_total``). The number is
 * the superintendent node the agent works for. Without a role a numbered node
 * still reads "Agent 2/5"; null when there is nothing to say.
 */
export function agentLabelFromPayload(payload: Record<string, unknown> | null | undefined): string | null {
  const i = payload?.node_index;
  const n = payload?.node_total;
  const num = typeof i === "number" && typeof n === "number" && n > 1 ? `${i}/${n}` : "";
  const kindRaw = typeof payload?.agent_kind === "string" ? payload.agent_kind : "";
  const kind = kindRaw
    ? kindRaw.charAt(0).toUpperCase() + kindRaw.slice(1)
    : (AGENT_KIND_OF_ROLE[typeof payload?.role === "string" ? payload.role : ""] ?? "");
  if (kind === "Librarian") return num ? `Librarian (Drafter ${num})` : "Librarian";
  if (kind) return num ? `${kind} ${num}` : kind;
  return num ? `Agent ${num}` : null;
}

/** Prefix ``text`` with the payload's agent label ("Agent 2/5 · …") when it has one. */
export function withAgentLabel(text: string, payload: Record<string, unknown> | null | undefined): string {
  const label = agentLabelFromPayload(payload);
  return label ? `${label} · ${text}` : text;
}

/** One ``submit_draft`` attempt, as the server reports it on ``tool_call_result``
 *  (``section_response.submit``) and persists on the Action History call. */
export interface SubmitAttempt {
  attempt?: number;
  /** accepted | staged | rerouted | rejected | pending */
  outcome?: string;
  declared?: number;
  received?: number;
  missing?: string[];
}

const SUBMIT_OUTCOME_TEXT: Record<string, string> = {
  accepted: "accepted",
  staged: "partial, staged",
  rerouted: "rerouted to another agent",
  rejected: "rejected, retrying",
  pending: "not accepted yet",
};

/** Read a {@link SubmitAttempt} off an untyped payload field; null when absent. */
export function submitAttemptOf(v: unknown): SubmitAttempt | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as SubmitAttempt) : null;
}

/**
 * Tell repeated submits apart: attempt number, what the server made of it, and
 * how many of the agent's sections have arrived — e.g.
 * ``Submission #2 · partial, staged · 3/7 sections (pending 9.2, 9.3)``.
 */
export function describeSubmitAttempt(s: SubmitAttempt): string {
  const parts: string[] = [s.attempt ? `Submission #${s.attempt}` : "Submission"];
  const outcome = s.outcome ?? "";
  parts.push(SUBMIT_OUTCOME_TEXT[outcome] ?? (outcome || "submitted"));
  if (typeof s.declared === "number" && s.declared > 0) {
    let progress = `${s.received ?? 0}/${s.declared} sections`;
    const missing = Array.isArray(s.missing) ? s.missing : [];
    if (missing.length > 0 && outcome !== "accepted") {
      progress += ` (pending ${missing.slice(0, 4).join(", ")}${missing.length > 4 ? "…" : ""})`;
    }
    parts.push(progress);
  }
  return parts.join(" · ");
}
